import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { 
  User, 
  Mail, 
  Calendar, 
  Crown, 
  Heart, 
  Clock, 
  BookOpen,
  PenLine,
  ChevronRight,
  BarChart3,
  Loader2,
  Award,
  Settings as SettingsIcon
} from 'lucide-react';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    favorites: 0,
    history: 0,
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [favRes, histRes] = await Promise.all([
          api.get('/favorites/count'),
          api.get('/history/count')
        ]);
        setStats({
          favorites: favRes.data.count,
          history: histRes.data.count,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);

  const menuItems = [
    {
      icon: SettingsIcon,
      label: 'Definições da Conta',
      href: '/conta',
      description: 'Subscrição, palavra-passe, eliminar conta',
      color: 'text-foreground'
    },
    { 
      icon: Heart, 
      label: 'Os Meus Favoritos', 
      href: '/favorites',
      description: stats.loading ? '...' : `${stats.favorites} contos guardados`,
      color: 'text-ruby'
    },
    { 
      icon: Clock, 
      label: 'Histórico de Leitura', 
      href: '/history',
      description: stats.loading ? '...' : `${stats.history} contos lidos`,
      color: 'text-blue-400'
    },
    { 
      icon: Award, 
      label: 'Pontos de Fidelidade', 
      href: '/points',
      description: 'Ganhe recompensas',
      color: 'text-gold'
    },
    { 
      icon: PenLine, 
      label: 'Escrever Conto', 
      href: '/write',
      description: 'Partilha a sua história',
      color: 'text-green-400'
    },
  ];

  if (user?.is_author) {
    menuItems.push({ 
      icon: BookOpen, 
      label: 'Os Meus Contos', 
      href: '/author',
      description: 'Gerir publicações',
      color: 'text-purple-400'
    });
    menuItems.push({ 
      icon: BarChart3, 
      label: 'Analytics', 
      href: '/author/analytics',
      description: 'Estatísticas das histórias',
      color: 'text-cyan-400'
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 sm:pt-24 pb-24 sm:pb-12 px-4 sm:px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Profile Header */}
          <div className="bg-surface rounded-2xl border border-border p-4 sm:p-8 mb-6">
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-ruby to-ruby-light flex items-center justify-center shadow-lg shrink-0">
                <span className="text-foreground text-xl sm:text-2xl font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-brand text-xl sm:text-2xl font-bold text-foreground truncate">
                  {user?.name}
                </h1>
                <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {user?.is_premium && (
                    <span className="premium-badge inline-flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                  {user?.is_author && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                      <PenLine className="w-3 h-3" />
                      Autor
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="bg-surface-light rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  {stats.loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-ruby" />
                  ) : (
                    <Heart className="w-5 h-5 text-ruby" />
                  )}
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.loading ? '-' : stats.favorites}
                </p>
                <p className="text-xs text-muted-foreground">Favoritos</p>
              </div>
              <div className="bg-surface-light rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  {stats.loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-blue-400" />
                  )}
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.loading ? '-' : stats.history}
                </p>
                <p className="text-xs text-muted-foreground">Lidos</p>
              </div>
              <div className="bg-surface-light rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Calendar className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-lg font-bold text-foreground">
                  {new Date(user?.created_at).toLocaleDateString('pt-PT', {
                    month: 'short',
                    year: '2-digit'
                  })}
                </p>
                <p className="text-xs text-muted-foreground">Desde</p>
              </div>
            </div>

            {!user?.is_premium && (
              <Button
                onClick={() => navigate('/premium')}
                data-testid="profile-premium-cta"
                className="w-full mt-6 bg-gradient-to-r from-ruby to-gold text-white rounded-full py-6 font-semibold hover:opacity-90 transition-opacity"
              >
                <Crown className="w-4 h-4 mr-2" />
                Desbloquear Premium
              </Button>
            )}
          </div>

          {/* Menu */}
          <div className="bg-surface rounded-2xl border border-border overflow-hidden mb-6">
            <h2 className="px-4 py-3 text-sm font-medium text-muted-foreground border-b border-border">
              A Minha Conta
            </h2>
            {menuItems.map((item, index) => (
              <button
                key={item.href}
                onClick={() => navigate(item.href)}
                data-testid={`profile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-4 p-4 hover:bg-surface-light transition-colors ${
                  index !== menuItems.length - 1 ? 'border-b border-border' : ''
                }`}
              >
                <div className={`w-10 h-10 rounded-xl bg-surface-light flex items-center justify-center`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <div className="flex-1 text-left">
                  <span className="text-foreground font-medium block">{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.description}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            ))}
          </div>

          {/* Logout */}
          <Button
            variant="outline"
            onClick={logout}
            data-testid="profile-logout"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full py-6"
          >
            Sair da conta
          </Button>
        </div>
      </main>
    </div>
  );
}
